<template>
  <div
    :id="id"
    ref="container-element"
    :tabindex="props.submenu ? undefined : 0"
    class="modern-color-theme font-poppins max-w-lg p-1 bg-neutral-100 flex flex-col overflow-y-auto max-h-96 z-[1001] outline-none"
    :class="[computedLayout.class, theme.container]"
    :style="computedLayout.style"
    data-component-name="VContextMenu"
    @keydown.esc="emit('close')"
  >
    <ul role="menubar">
      <li v-for="(item, index) of flatItems" ref="child-ref" :key="index" role="menuitem">
        <template v-if="item.type === 'button' || item.type === 'checkbox' || item.type === 'menu' || item.type === 'label'">
          <VSContextMenuItem :item="item" :theme="theme.__key" :context="props.context" v-on="getListeners(item.type === 'menu' ? index : undefined)" @close="emit('close')" />
          <VContextMenu v-if="item.type === 'menu' && !item.disabled && submenuIndex === index" :theme="props.theme" :context="props.context" :position="getSubmenuPosition(index)" :controller="props.controller || containerElement" :width="props.width" :items="item.items" submenu @close="emit('close')" />
        </template>
        <template v-else-if="item.type === 'draggable'">
          <VueDraggable v-model="item.items" item-key="key">
            <template #item="{element: child}: {element: V.ContextMenu.Draggable['items'][number]}">
              <div :key="child.key">
                <VSContextMenuItem :item="child" :theme="theme.__key" v-on="getListeners(undefined)">
                  <VIcon size="xs" name="Outline/drag-indicator-vertical" />
                </VSContextMenuItem>
              </div>
            </template>
          </VueDraggable>
        </template>
        <template v-else-if="item.type === 'separator'">
          <hr>
        </template>
        <template v-else-if="item.type === 'search'">
          <VSearch v-model="item.value" :placeholder="item.placeholder" @input="submenuIndex = undefined; recomputeFlatItems()" />
        </template>
      </li>
    </ul>
  </div>
</template>
<script setup generic="T" lang="ts">
import VContextMenu from './VContextMenu.vue';
import { computed, onMounted, ref, toRef } from 'vue';
import { onClickOutsideOf } from '@component-utils/focus';
import VueDraggable from '@libs/zhyswan-vuedraggable/vuedraggable'
import { getFilteredCollection } from '@component-utils/search';
import type { V } from '@component-utils/types';
import { useManualTracking, useTemplateRef } from '@component-utils/reactivity';
import { useElementId } from '@component-utils/utils';
import VSearch from '@component-library/inputs/VSearch.vue'
import { getItemValue } from '../../utils/utils';
import VSContextMenuItem from './components/VSContextMenuItem.vue';
import VIcon from '@component-library/labels/VIcon.vue';
import { getDOMRect } from '@component-utils/position';
import { useWindowSize } from '@component-utils/window';
import type { ThemeProp } from '@component-library/themes/types';
import { useTheme } from '@component-library/themes/themes';

defineOptions({
  name: 'VContextMenu'
})

const props = withDefaults(
  defineProps<{
    /**
     * What items to display 
     */
    items: V.ContextMenu.Item<T>[]
    /**
     * Position of the context menu
     */
    position: 'absolute' | ({ [K in 'right' | 'left' | 'top' | 'bottom' | 'centerHorizontal']?: number } & { container?: { width: number, height: number } })
    /**
     * A value that is passed to action methods when buttons are clicked
     */
    context?: T
    /**
     * Width of context menu in pixels
     */
    width?: number
    /**
     * Theme
     */
    theme?: ThemeProp<'VContextMenu'>
    /**
     * FOR INTERNAL USE ONLY
     * 
     * Indicates a controller of this context menu
     */
    controller?: HTMLElement | null
    /**
     * FOR INTERNAL USE ONLY
     * 
     * Indicates whether context menu is a submenu
     */
    submenu?: boolean
  }>(),
  {
    width: 250,
    context: undefined,
    controller: undefined,
    theme: undefined
  }
)

const id = useElementId(undefined)

const theme = useTheme(props, 'VContextMenu')

const getListeners = (value: number | undefined) => ({
  mouseenter: () => submenuIndex.value = value,
  mouseleave: () => {},
  focus: () => submenuIndex.value = value,
  blur: () => {}
})

const submenuIndex = ref<number>()

const emit = defineEmits<{
  close: []
}>()

defineExpose({
  get element () { return containerElement }
})

const items = toRef(props.items)

const { computedRef: flatItems, recompute: recomputeFlatItems } = useManualTracking(() => {
  return items.value.flatMap<V.ContextMenu.Item<T>>((item) => {
    if (item.type === 'search') {
      return [
        item,
        ...getFilteredCollection(item.items, item.value, ['label'], { fuzzy: true })
      ]
    } else if (item.type === 'sortable') {
      return item.items.toSorted((item1, item2) => +getItemValue(item2) - +getItemValue(item1))
    } else {
      return item
    }
  })
})

onMounted(() => {
  // Clear searches on mount
  for (const item of flatItems.value) {
    if (item.type === 'search') item.value = ''
  }
})

const containerElement = useTemplateRef('container-element')

onClickOutsideOf(
  [
    containerElement
  ],
  () => {
    if (props.controller) {
      // Do not close it on focus loss as it is controlled by the controller element
    } else {
      emit('close')
    }
  },
  {
    esc: true
  }
)

const windowSize = useWindowSize()

const computedLayout = computed(() => {
  const menuWidth = props.width

  if (props.position === 'absolute') {
    return {
      class: 'absolute',
      style: {
        minWidth: `${menuWidth}px`,
        width: `${menuWidth}px`
      }
    }
  } else {
    const menuHeight = Math.min(384, 36 * flatItems.value.length)

    const { right: windowWidth, bottom: windowHeight } = windowSize.value

    const position: { [K in 'right' | 'left' | 'top' | 'bottom']?: string } = {}

    const { height: containerHeight } = props.position.container ?? { width: 0, height: 0 }

    if (typeof props.position.centerHorizontal === 'number') {
      const left = props.position.centerHorizontal - menuWidth / 2
      const right = props.position.centerHorizontal + menuWidth / 2

      if (left + menuWidth > windowWidth) {
        if (right - menuWidth < 0) {
          position.left = `${right}px`
        } else {
          position.right = `${windowWidth - right}px`
        }
      } else {
        position.left = `${left}px`
      }
    } else if (typeof props.position.left === 'number') {
      const left = props.position.left

      if (left + menuWidth > windowWidth) {
        position.right = `${windowWidth - left}px`
      } else {
        position.left = `${left}px`
      }
    } else if (typeof props.position.right === 'number') {
      const right = props.position.right

      if (right - menuWidth < 0) {
        position.left = `${right}px`
      } else {
        position.right = `${windowWidth - right}px`
      }
    }

    if (typeof props.position.top === 'number' && typeof props.position.bottom === 'number') {
      const top = props.position.top
      const bottom = props.position.bottom

      if (top + menuHeight > windowHeight) {
        if (bottom - menuHeight < 0) {
          position.top = `${bottom}px`
        } else {
          position.bottom = `${windowHeight - bottom}px`
        }
      } else {
        position.top = `${top}px`
      }
    } else if (typeof props.position.top === 'number') {
      const top = props.position.top

      if (top + menuHeight > windowHeight) {
        position.bottom = `${windowHeight - top + containerHeight}px`
      } else {
        position.top = `${top}px`
      }
    } else if (typeof props.position.bottom === 'number') {
      const bottom = props.position.bottom

      if (bottom - menuHeight < 0) {
        position.top = `${bottom}px`
      } else {
        position.bottom = `${windowHeight - bottom}px`
      }
    }

    return {
      class: 'fixed',
      style: Object.assign(
        {
          minWidth: `${menuWidth}px`,
          width: `${menuWidth}px`
        },
        position
      )
    }
  }
})

const childrenRefs = useTemplateRef('child-ref')

const getSubmenuPosition = (index: number) => {
  const button = childrenRefs.value?.[index]
  if (button) {
    const rect = getDOMRect(button)
    const menuWidth = props.width

    const position: { [K in 'right' | 'left' | 'top' | 'bottom']?: number } = {
      top: rect.top - 4,
      bottom: rect.bottom + 4,
    }

    const { right: windowWidth } = windowSize.value

    if (rect.right + menuWidth > windowWidth) {
      position.right = rect.left
    } else {
      position.left = rect.right
    }

    return position
  } else {
    throw new Error(`Submenu position not available for index ${index}`)
  }
}
</script>
